import React, { useState, useEffect } from 'react'

import { Card, Stat, Notice } from './ui'
import PositionImpact from './PositionImpact'
import PositionWeekGrid from './PositionWeekGrid'
import WeekScrubber from './WeekScrubber'
import LineupTable from './LineupTable'
import BottomDrawer from './BottomDrawer'
import { useIsCompact } from '../lib/useMediaQuery'
import {
  signedPoints,
  signedPercent,
  percent,
  points,
  adp,
  deltaClass,
  formatLabel,
} from '../lib/format'

// The draft-cost half of the summary card: what each side's players cost on
// this season's draft board. It sits inside "Per week, on average" rather than
// in a panel of its own because it answers the same question the stats above
// it do — was this a fair deal for this team — and as a separate card it sat
// below three charts, too far away to read against them.
//
// Received and given stack rather than sitting side by side: the summary card
// is a third of the row on a wide screen, and two columns of player names
// truncate to nothing at that width.
function DraftCost({ adpForTeam, phantomIds, teamName }) {
  if (!adpForTeam) return null

  const { received = [], given = [], delta, format } = adpForTeam

  return (
    <div className="mt-4 border-t pt-3 border-slate-700">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-300">
        Draft cost
      </div>
      <p className="mt-0.5 text-xs text-slate-500">
        Average draft position on the {formatLabel(format)} board
      </p>

      <div className="mt-2 space-y-3">
        <AdpList label="Received" entries={received} />
        <AdpList label="Given up" entries={given} phantomIds={phantomIds} />
      </div>

      <p className="mt-3 text-sm text-slate-300">
        {delta === null || delta === undefined ? (
          'Not enough draft data to compare — at least one player went undrafted.'
        ) : delta > 0 ? (
          <>
            {/* Named rather than "you": this panel is shown for whichever tab is
                open, which in a multi-team trade is usually not your own team,
                and on a shared link is never anybody in particular. */}
            {teamName} is acquiring players drafted{' '}
            <strong className="tabular-nums">{Math.abs(delta).toFixed(1)}</strong> picks
            earlier than the ones it gives up.
          </>
        ) : (
          <>
            {teamName} is giving up players drafted{' '}
            <strong className="tabular-nums">{Math.abs(delta).toFixed(1)}</strong> picks
            earlier than the ones it receives.
          </>
        )}
      </p>
    </div>
  )
}

// Where each traded player stands at his own position.
//
// The fourth lens, and the one that makes the two sides of a deal comparable at
// all. Points by position says what each slot gains, win probability says what
// that is worth, draft cost says what the market charged. None of them answers
// the question a manager asks first — is the back coming back better *for a
// back* than the receiver going out is *for a receiver* — because raw points do
// not compare across positions. In a typical league the average rostered
// quarterback is worth nearly three times the average kicker.
//
// Absent on evaluations computed before this lens shipped, hence the guard.
function PositionalStanding({ positionalForTeam, teamName }) {
  if (!positionalForTeam) return null

  const { received = [], given = [], delta } = positionalForTeam
  if (received.length === 0 && given.length === 0) return null

  return (
    <div className="mt-4 border-t pt-3 border-slate-700">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-300">
        Against their position
      </div>
      <p className="mt-0.5 text-xs text-slate-500">
        Points per week above the average at each player&apos;s own position
      </p>

      <div className="mt-2 space-y-3">
        <StandingList label="Received" entries={received} />
        <StandingList label="Given up" entries={given} />
      </div>

      <p className="mt-3 text-sm text-slate-300">
        {delta === null || delta === undefined ? (
          'No position in this league has enough rostered players to compare against.'
        ) : delta > 0 ? (
          <>
            {teamName} is acquiring players who stand{' '}
            <strong className="tabular-nums">{Math.abs(delta).toFixed(1)}</strong> points a
            week further above their positions than the ones it gives up.
          </>
        ) : (
          <>
            {teamName} is giving up players who stand{' '}
            <strong className="tabular-nums">{Math.abs(delta).toFixed(1)}</strong> points a
            week further above their positions than the ones it receives.
          </>
        )}
      </p>
    </div>
  )
}

// At a position whose startable players sit closer together than their own
// projections move week to week, the tier is real arithmetic about
// interchangeable players. Rendering "K tier 1" invites someone to trade for a
// kicker, so the caveat replaces the rank rather than sitting beside it.
function tierLabel(entry) {
  if (entry.streamable) return 'streamed'
  if (!entry.tier) return ''
  if (entry.tier === 'waiver') return 'waiver'

  return `tier ${entry.tier}`
}

function StandingList({ label, entries }) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </h4>
      <ul className="space-y-1 text-sm">
        {entries.map((entry) => (
          <li key={entry.player_id} className="flex justify-between gap-2">
            <span className="truncate text-slate-200">
              {entry.name}
              <span className="ml-1 text-xs text-slate-400">
                {entry.position}
                {tierLabel(entry) ? ` ${tierLabel(entry)}` : ''}
              </span>
            </span>
            <span className={`tabular-nums ${deltaClass(entry.above_average)}`}>
              {signedPoints(entry.above_average)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// phantomIds is only ever passed for the outgoing side — a team cannot receive
// a player it already fails to roster.
function AdpList({ label, entries, phantomIds }) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </h4>
      <ul className="space-y-1 text-sm">
        {entries.map((entry) => (
          <li key={entry.player_id} className="flex justify-between gap-2">
            <span className="truncate text-slate-200">
              {entry.name}
              <span className="ml-1 text-xs text-slate-400">{entry.position}</span>
              {phantomIds?.has(entry.player_id) && (
                <span className="ml-1 text-xs italic text-slate-400">
                  not on this roster
                </span>
              )}
            </span>
            <span className="tabular-nums text-slate-300">
              {adp(entry.adp)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function WeekDetail({ weekResult, teamId, incomingIds }) {
  const view = weekResult?.teams?.[teamId]
  if (!view) return null

  const before = view.before || {}
  const after = view.after || {}
  const hasOpponent = view.opponent_id

  return (
    <Card
      title={`Week ${weekResult.week}`}
      subtitle={
        hasOpponent
          ? `vs ${view.opponent_name} — projected ${points(view.opponent_points)}`
          : 'No opponent scheduled this week'
      }
    >
      {hasOpponent && (
        <div className="mb-4 grid grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:items-center sm:gap-6">
          <Stat label="Win % before" value={percent(before.win_probability)} />
          <Stat label="Win % after" value={percent(after.win_probability)} />
          <Stat
            label="Change"
            value={signedPercent(view.delta?.win_probability)}
            tone={deltaClass(view.delta?.win_probability)}
          />
          <Stat
            label="Points"
            value={signedPoints(view.delta?.points)}
            tone={deltaClass(view.delta?.points)}
          />
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2">
        <LineupTable label="Before" lineup={before.lineup} total={before.points} />
        <LineupTable
          label="After"
          lineup={after.lineup}
          total={after.points}
          highlightIds={incomingIds}
        />
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Both lineups are the best legal lineup for the week, opponents included.
      </p>
    </Card>
  )
}

// Everything about an evaluation that reads the same whether you own the trade
// or opened a shared link: the team toggle, the charts, the week detail and the
// draft cost. Owning the teamId/week state here is what lets both callers stay
// thin — they differ only in the chrome above this, and in whether the payload
// they hand over has been redacted.
//
// `trade` is the trade object itself, not a query result. `preferredTeamId`
// only nudges the default tab; a shared view passes nothing and lands on the
// first side of the deal.
export default function TradeEvaluationView({ trade, results, preferredTeamId = null }) {
  const [teamId, setTeamId] = useState(null)
  const [week, setWeek] = useState(null)

  // 'week' | 'season' — which numbers the roster-position panel is showing.
  // Defaults to the selected week: the season average answers "was this a good
  // trade", which the summary card above already covers, while the week answers
  // "what does it do to the lineup I am looking at", which is the question the
  // rest of this view is about.
  const [positionScope, setPositionScope] = useState('week')

  // The week grid is several rows of buttons, which on a phone is a large
  // share of a screen the bar is already pinned over. Collapsed there by
  // default; open on a wide screen, where it costs nothing.
  //
  // null means "follow the breakpoint" rather than defaulting to a boolean, so
  // rotating a phone does not strand the bar in the state the other
  // orientation wanted — and once someone toggles it, their choice sticks.
  const compact = useIsCompact()
  const [weeksOverride, setWeeksOpen] = useState(null)
  const weeksOpen = weeksOverride ?? !compact

  // Picking a week anywhere — the scrubber, the grid, a "weeks better" chip —
  // pulls the position panel back to that week. Otherwise the panel would sit
  // on the season average while every other panel moved, and silently stop
  // answering the question the user just asked.
  const selectWeek = (value) => {
    setWeek(value)
    setPositionScope('week')
  }

  const teams = results?.teams || []
  const weeks = results?.weeks || []

  useEffect(() => {
    if (teamId || teams.length === 0) return
    const preferred = teams.find((t) => String(t.id) === String(preferredTeamId)) || teams[0]
    setTeamId(preferred.id)
  }, [teams, preferredTeamId, teamId])

  useEffect(() => {
    if (week || weeks.length === 0) return
    setWeek(weeks[0].week)
  }, [weeks, week])

  if (weeks.length === 0) {
    return (
      <Card title="Nothing left to evaluate">
        <p className="text-sm text-slate-300">
          {results?.meta?.note || 'The regular season is over.'}
        </p>
      </Card>
    )
  }

  const summary = results.summary?.[teamId] || {}
  const weekResult = weeks.find((w) => w.week === week) || weeks[0]

  // What the roster-position panel is currently describing. A season figure is
  // an average per week; a single week's is that week's own points, so the two
  // carry different units and the net label below says which.
  const showingSeason = positionScope === 'season'
  const weekDelta = weekResult?.teams?.[teamId]?.delta
  const positionValues = showingSeason
    ? summary.points_by_position
    : weekDelta?.points_by_position
  const positionNet = showingSeason ? summary.points_per_week : weekDelta?.points
  const deltasByWeek = Object.fromEntries(
    weeks.map((w) => [w.week, w.teams?.[teamId]?.delta?.win_probability])
  )

  // Which weeks got better, not how many — same rule the summary counts with:
  // a positive points delta.
  const improvedWeeks = weeks
    .filter((w) => (w.teams?.[teamId]?.delta?.points ?? 0) > 0)
    .map((w) => w.week)

  const assets = trade?.assets || []

  const incomingIds = assets
    .filter((asset) => String(asset.to_team_id) === String(teamId))
    .map((asset) => asset.player_id)

  const exploratory = Boolean(trade?.exploratory)
  const teamName = teams.find((t) => String(t.id) === String(teamId))?.name || 'This team'

  // Players this side nominally gives up but does not actually roster. Only an
  // exploratory trade can produce them, and they cost it nothing — the lineup
  // it loses them from never held them. Saying so is the difference between a
  // sandbox and a bug.
  const phantomOutgoing = assets.filter(
    (asset) =>
      String(asset.from_team_id) === String(teamId) &&
      String(asset.owner_team_id ?? '') !== String(teamId)
  )
  const phantomIds = new Set(phantomOutgoing.map((asset) => asset.player_id))

  return (
    <>
      {/* Two teams split the bar into equal centered segments — otherwise the
          names hug the left edge of a wide bar. Past two, equal segments on a
          phone leave ~80px each and every name truncates to nothing, so the bar
          scrolls instead and each tab keeps its full width. */}
      <div
        className={`mb-4 flex w-full rounded-md border border-slate-600 ${
          teams.length > 2 ? 'overflow-x-auto' : ''
        }`}
      >
        {teams.map((team) => (
          <button
            key={team.id}
            type="button"
            onClick={() => setTeamId(team.id)}
            className={`min-w-0 truncate px-3 py-2 text-center text-sm first:rounded-l-md last:rounded-r-md ${
              teams.length > 2 ? 'shrink-0' : 'flex-1'
            } ${
              String(team.id) === String(teamId)
                ? 'bg-slate-100 text-slate-900'
                : 'text-slate-300'
            }`}
          >
            {team.name}
          </button>
        ))}
      </div>

      {results.meta?.projections_present === false && (
        <div className="mb-4">
          <Notice title="No projections were available for these weeks">
            Every point total and win probability below is zero because the
            projection data is missing — not because the trade is neutral. Run{' '}
            <code className="font-mono">bin/rails sleeper:bootstrap</code>, then
            reload.
          </Notice>
        </div>
      )}

      {exploratory && phantomOutgoing.length > 0 && (
        <div className="mb-4">
          <Notice title={`${teamName} doesn’t roster everyone on its side`}>
            {phantomOutgoing.map((asset) => asset.player_name).join(', ')}{' '}
            {phantomOutgoing.length === 1 ? 'belongs' : 'belong'} to another team,
            so this lineup loses nothing for them — while the side receiving them
            still gains them. The sides will not add up, which is what an
            exploration is for.
          </Notice>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3 lg:items-start">
        <Card title="Per week, on average" className="lg:col-span-1">
          <div className="grid grid-cols-2 gap-4">
            <Stat
              label="Points"
              value={signedPoints(summary.points_per_week)}
              tone={deltaClass(summary.points_per_week)}
            />
            <Stat
              label="Win odds"
              value={signedPercent(summary.win_probability_per_week)}
              tone={deltaClass(summary.win_probability_per_week)}
            />
            <div className="col-span-2">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Weeks better
              </div>
              {improvedWeeks.length === 0 ? (
                <div className="mt-0.5 text-xl font-semibold">None</div>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1">
                  {improvedWeeks.map((week) => (
                    <button
                      key={week}
                      type="button"
                      onClick={() => selectWeek(week)}
                      className="rounded-md px-1.5 py-1 text-xs font-semibold tabular-nums border border-slate-700 bg-slate-900 hover:border-slate-500"
                    >
                      Wk {week}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <PositionalStanding
            positionalForTeam={results.positional?.[teamId]}
            teamName={teamName}
          />

          <DraftCost
            adpForTeam={results.adp?.[teamId]}
            phantomIds={phantomIds}
            teamName={teamName}
          />
        </Card>

        <Card
          title="By roster position"
          subtitle={
            showingSeason
              ? 'Season average, per week'
              : `Week ${weekResult.week} · pick a week below to change this`
          }
          className="lg:col-span-2"
        >
          <div className="mb-3 inline-flex rounded-md border border-slate-600">
            <button
              type="button"
              onClick={() => setPositionScope('week')}
              aria-pressed={!showingSeason}
              className={`rounded-l-md px-3 py-1 text-xs font-medium ${
                showingSeason ? 'text-slate-300 hover:text-slate-100' : 'bg-slate-100 text-slate-900'
              }`}
            >
              Week {weekResult.week}
            </button>
            <button
              type="button"
              onClick={() => setPositionScope('season')}
              aria-pressed={showingSeason}
              className={`rounded-r-md px-3 py-1 text-xs font-medium ${
                showingSeason ? 'bg-slate-100 text-slate-900' : 'text-slate-300 hover:text-slate-100'
              }`}
            >
              Season
            </button>
          </div>

          <PositionImpact
            byPosition={positionValues}
            net={positionNet}
            netLabel={showingSeason ? 'points per week' : 'points this week'}
          />
        </Card>
      </div>

      <div className="mt-4">
        <Card
          title="Position impact, week by week"
          subtitle="Points gained or lost at each roster position. Bars run up for a gain, down for a loss, and every row shares one scale."
        >
          <PositionWeekGrid
            weeks={weeks}
            teamId={teamId}
            averages={summary.points_by_position}
            selectedWeek={weekResult.week}
            onSelectWeek={selectWeek}
          />
        </Card>
      </div>

      <div className="mt-4">
        <WeekDetail weekResult={weekResult} teamId={teamId} incomingIds={incomingIds} />
      </div>

      {/*
        The week selector, pinned to the bottom of the viewport.

        Every panel on this page answers "for which week?", and the answer used
        to live in a card you had to scroll back up to. Sticky, it is reachable
        from wherever you are reading — and it is last in the flow so the
        containing block extends above it, which is what makes `bottom-0` pin
        it from the top of the page rather than only once you scroll to it.

        The negative margins undo the Layout's own padding so the bar spans the
        full width, the way a chrome element should.
      */}
      <p className="mt-6 text-xs text-slate-500">
        Projections from {results.meta?.projection_source}, scored under this league's own rules.
        Win probability is computed here — no fantasy platform publishes one.
        {results.meta?.variance_calibrated
          ? ` Spread calibrated on ${results.meta.variance_samples} historical results.`
          : ' Spread uses positional priors until this season has results to learn from.'}
      </p>

      {/*
        The week selector, pinned to the bottom of the viewport.

        Every panel above answers "for which week?", and the answer used to sit
        in a card you had to scroll back up to. Sticky, it is reachable from
        wherever you are reading.

        Last in the flow on purpose. `bottom-0` pins an element only while its
        containing block still extends below it — placed mid-page it would
        unstick as soon as you scrolled past its own position, which is the
        opposite of what a chrome bar should do.

        The negative margins undo Layout's padding so the bar spans the full
        width the way chrome does, rather than sitting inset like a card.
      */}
      {/*
        pb uses env(safe-area-inset-bottom) because on a phone the browser
        reserves that strip for the home indicator, and a bar that ignores it
        has its last row of buttons sitting underneath — which reads as the bar
        being cut off, because it is. max() keeps the ordinary padding on every
        device that has no inset.
      */}
      <BottomDrawer
        open={weeksOpen}
        onOpenChange={setWeeksOpen}
        summary={
          <div className="flex items-baseline justify-between gap-3">
            <span className="flex items-baseline gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Week {weekResult.week}
              </span>
              <span
                className={`text-xs font-semibold tabular-nums ${deltaClass(deltasByWeek[weekResult.week])}`}
              >
                {signedPercent(deltasByWeek[weekResult.week])}
              </span>
            </span>

            <span className="shrink-0 text-xs text-slate-500">
              {weeksOpen ? 'drag down to hide' : 'drag up to change week'}
            </span>
          </div>
        }
      >
        <WeekScrubber
          weeks={weeks.map((w) => w.week)}
          selected={weekResult.week}
          onSelect={selectWeek}
          deltasByWeek={deltasByWeek}
        />
      </BottomDrawer>
    </>
  )
}
